import { createServerFn } from "@tanstack/react-start"

import { getSupabaseAdminClient } from "@repo/supabase/admin-client"
import { requireUser } from "@repo/supabase/require-user"
import { getSupabaseServerClient } from "@repo/supabase/server-client"
import { logger } from "@repo/utils/logger"
import { authSessionMiddleware } from "~/lib/middleware/authenticated-session.middleware"
import { captchaTokenMiddleware } from "~/lib/middleware/captcha-token.middleware"
import { errorObject, successObject } from "~/lib/utils/http"
import { createUnitsService } from "../units/units.service"
import {
  acceptInvitationSchema,
  createInvitationSchema,
  getInvitationSchema,
  resendInvitationSchema,
} from "./invitations.schema"
import { createInvitationsService } from "./invitations.service"
import { sendInvitationEmail } from "./send-invitation-email"

const log = logger.child({
  module: "invitations-functions",
})

// This function is public and can be called by anyone
export const getInvitationByTokenFn = createServerFn({ method: "GET" })
  .inputValidator(getInvitationSchema)
  .handler(async ({ data: { token } }) => {
    const supabase = getSupabaseAdminClient()
    const invitationsService = createInvitationsService(supabase)

    try {
      const invitation = await invitationsService.getInvitation({ token })

      return successObject(invitation)
    } catch (error) {
      log.error("Failed to get invitation", { error })
      return errorObject("Failed to get invitation")
    }
  })

export const acceptInvitationFn = createServerFn({ method: "POST" })
  .middleware([captchaTokenMiddleware])
  .inputValidator(acceptInvitationSchema)
  .handler(async ({ data: { token, otp } }) => {
    const supabase = getSupabaseServerClient()

    log.info("Accepting invitation", {
      tokenLength: token.length,
      otpLength: otp?.length || 0,
    })

    // Check if user is authenticated
    const { user, error: authError } = await requireUser(supabase)

    // If not authenticated, OTP is required
    if (authError || !user) {
      if (!otp) {
        log.warn(
          "Unauthenticated user attempted to accept invitation without OTP",
        )
        return errorObject(
          "Authentication required. Please provide the one-time password from your email.",
        )
      }

      // Get invitation to retrieve the email
      const supabaseAdmin = getSupabaseAdminClient()
      const invitationsService = createInvitationsService(supabaseAdmin)
      const invitation = await invitationsService
        .getInvitation({ token })
        .catch((error) => {
          log.error("Failed to get invitation", { error })
          return null
        })

      if (!invitation || !invitation.active) {
        log.warn("Invalid invitation token", {
          tokenPrefix: token.slice(0, 6),
          tokenLength: token.length,
        })
        return errorObject("Invitation not found or expired")
      }

      log.info("Verifying OTP for invitation acceptance", {
        inviteeUserId: invitation.invitee_user_id ?? null,
      })

      const { error: otpError } = await supabase.auth.verifyOtp({
        token: otp,
        email: invitation.invitee_email,
        type: "email",
      })

      if (otpError) {
        log.error("Failed to verify OTP", { error: otpError })
        return errorObject(
          "Invalid or expired one-time password. Please check your email and try again.",
        )
      }

      log.info("OTP verified successfully for invitation acceptance")
    }

    // User is now authenticated (either was before, or just authenticated via OTP)
    const invitationsService = createInvitationsService(supabase)

    try {
      const invitation = await invitationsService.acceptInvitation({ token })

      log.info("Invitation accepted", {
        invitationId: invitation.invitation_id,
      })

      return successObject(invitation)
    } catch (error) {
      log.error("Failed to accept invitation", { error })
      return errorObject("Failed to accept invitation")
    }
  })

export const createInvitationFn = createServerFn({ method: "POST" })
  .middleware([authSessionMiddleware])
  .inputValidator(createInvitationSchema)
  .handler(
    async ({
      data: {
        email,
        name,
        roleId,
        systemRoleId,
        unitId,
        parentUnitId,
        parentRoleId,
        metadata,
      },
      context,
    }) => {
      const invitationsService = createInvitationsService(context.supabase)

      try {
        // Query to find out if the invitee user already exists
        let inviteeUserId: string | undefined
        const supabaseAdmin = getSupabaseAdminClient()
        const unitsService = createUnitsService(supabaseAdmin)
        const units = await unitsService.getUnitsByEmail({
          email,
          unitType: "personal",
        })

        if (units?.length) {
          const unit = units[0]
          inviteeUserId = unit.unit_id
        }

        const invitation = await invitationsService.createInvitation({
          unitId,
          roleId,
          parentUnitId: parentUnitId ?? undefined,
          parentRoleId: parentRoleId ?? undefined,
          systemRoleId: systemRoleId ?? undefined,
          inviteeName: name,
          inviteeEmail: email,
          inviteeUserId,
          metadata,
          type: "one_time",
        })

        log.info("Invitation created", {
          tokenPrefix: invitation.token.slice(0, 6),
          tokenLength: invitation.token.length,
          unitId,
          roleId,
          systemRoleId,
          parentUnitId,
          parentRoleId,
          inviteeEmail: email,
          inviteeUserId,
        })

        return successObject(invitation)
      } catch (error) {
        log.error("Failed to create invitation", {
          error,
          unitId,
          email,
          roleId,
          systemRoleId,
          parentUnitId,
          parentRoleId,
        })
        return errorObject("Failed to create invitation")
      }
    },
  )

export const resendInvitationFn = createServerFn({ method: "POST" })
  .middleware([captchaTokenMiddleware])
  .inputValidator(resendInvitationSchema)
  .handler(async ({ data: { token } }) => {
    const supabase = getSupabaseAdminClient()
    const invitationsService = createInvitationsService(supabase)

    try {
      const invitation = await invitationsService.getInvitation({ token })

      if (!invitation || !invitation.active) {
        log.warn("Invitation not found or expired", {
          tokenPrefix: token.slice(0, 6),
          tokenLength: token.length,
        })
        return errorObject("Invitation not found or expired")
      }

      log.info("Resending invitation email", {
        invitationId: invitation.id,
        tokenPrefix: token.slice(0, 6),
        tokenLength: token.length,
        inviteeEmail: invitation.invitee_email,
      })

      await sendInvitationEmail({
        invitation,
        token,
      })

      return successObject({ message: "Invitation resent successfully" })
    } catch (error) {
      log.error("Failed to resend invitation", {
        error,
        tokenPrefix: token.slice(0, 6),
        tokenLength: token.length,
      })
      return errorObject("Failed to resend invitation")
    }
  })
